import { useEffect, useId, useLayoutEffect, useRef, type MouseEvent, type ReactNode } from 'react';

import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'design-system';
import { EllipsisIcon, XIcon } from 'lucide-react';

import type { BaseTabType } from '../../types';

export type MarketWidgetAction = {
  id: string;
  label: string;
  icon: ReactNode;
  visible: boolean;
  locked?: boolean;
  lockedReason?: string;
  disabled?: boolean;
  onSelect: (anchor: HTMLElement) => void;
};

export type MarketWidgetTab = BaseTabType & {
  disabled?: boolean;
  tag?: ReactNode;
};

export type MarketWidgetSurfaceProps = {
  title: string;
  tabs: MarketWidgetTab[];
  activeTab: number;
  actions: MarketWidgetAction[];
  fullWidth?: boolean;
  fullscreen?: boolean;
  onTabChange: (index: number) => void;
  onLockedAction?: (action: MarketWidgetAction) => void;
  onRequestCloseFullscreen: () => void;
  children: ReactNode;
};

type ActionControlsProps = {
  actions: MarketWidgetAction[];
  directAnchorId: (actionId: string) => string;
  overflowAnchorId: string;
  overflowTriggerRef: React.RefObject<HTMLButtonElement | null>;
  onDirectAction: (action: MarketWidgetAction, anchor: HTMLButtonElement) => void;
  onOverflowAction: (action: MarketWidgetAction) => void;
};

type PendingAnchor = {
  id: string;
  token: symbol;
};

function OverflowAction({
  action,
  onSelect
}: {
  action: MarketWidgetAction;
  onSelect: (action: MarketWidgetAction) => void;
}) {
  const item = (
    <DropdownMenuItem
      aria-disabled={action.locked || undefined}
      disabled={action.disabled && !action.locked}
      onClick={() => onSelect(action)}
    >
      {action.icon}
      {action.label}
    </DropdownMenuItem>
  );

  if (!action.locked || !action.lockedReason) return item;

  return (
    <Tooltip>
      <TooltipTrigger render={item} />
      <TooltipContent>{action.lockedReason}</TooltipContent>
    </Tooltip>
  );
}

function ActionControls({
  actions,
  directAnchorId,
  overflowAnchorId,
  overflowTriggerRef,
  onDirectAction,
  onOverflowAction
}: ActionControlsProps) {
  const visibleActions = actions.filter(action => action.visible);
  if (visibleActions.length === 0) return null;

  return (
    <>
      <div className='hidden items-center gap-1 @sm/card-header:flex'>
        {visibleActions.map(action => (
          <IconButton
            key={action.id}
            id={directAnchorId(action.id)}
            label={action.label}
            tooltipContent={action.lockedReason}
            aria-disabled={action.locked || undefined}
            disabled={action.disabled && !action.locked}
            onClick={(event: MouseEvent<HTMLButtonElement>) =>
              onDirectAction(action, event.currentTarget)
            }
          >
            {action.icon}
          </IconButton>
        ))}
      </div>
      <div className='flex @sm/card-header:hidden'>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <IconButton
                ref={overflowTriggerRef}
                id={overflowAnchorId}
                label='More widget actions'
              />
            }
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {visibleActions.map(action => (
              <OverflowAction
                key={action.id}
                action={action}
                onSelect={onOverflowAction}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

/**
 * Controlled market-widget workspace composed from canonical design-system
 * card, tabs, action, overflow, and fullscreen-dialog primitives.
 */
function MarketWidgetSurface({
  title,
  tabs,
  activeTab,
  actions,
  fullWidth = false,
  fullscreen = false,
  onTabChange,
  onLockedAction,
  onRequestCloseFullscreen,
  children
}: MarketWidgetSurfaceProps) {
  const instanceId = useId();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);
  const pendingAnchorRef = useRef<PendingAnchor | null>(null);
  const pendingAnchorExpiryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const returnAnchorIdRef = useRef<string | null>(null);
  const previousFullscreenRef = useRef(fullscreen);
  const overflowAnchorId = `${instanceId}-overflow`;
  const directAnchorId = (actionId: string) => `${instanceId}-direct-${actionId}`;

  useLayoutEffect(() => {
    const wasFullscreen = previousFullscreenRef.current;

    if (!wasFullscreen && fullscreen) {
      if (pendingAnchorExpiryRef.current !== null) {
        clearTimeout(pendingAnchorExpiryRef.current);
        pendingAnchorExpiryRef.current = null;
      }
      returnAnchorIdRef.current = pendingAnchorRef.current?.id ?? null;
      pendingAnchorRef.current = null;
    }

    if (wasFullscreen && !fullscreen) {
      const returnAnchorId = returnAnchorIdRef.current;
      returnAnchorIdRef.current = null;
      if (returnAnchorId) document.getElementById(returnAnchorId)?.focus();
    }

    previousFullscreenRef.current = fullscreen;
  }, [fullscreen]);

  useEffect(
    () => () => {
      if (pendingAnchorExpiryRef.current !== null) {
        clearTimeout(pendingAnchorExpiryRef.current);
      }
    },
    []
  );

  const prepareReturnAnchor = (anchorId: string) => {
    if (pendingAnchorExpiryRef.current !== null) {
      clearTimeout(pendingAnchorExpiryRef.current);
      pendingAnchorExpiryRef.current = null;
    }

    const token = Symbol(anchorId);
    pendingAnchorRef.current = { id: anchorId, token };
    return token;
  };

  const scheduleReturnAnchorExpiry = (token: symbol) => {
    if (pendingAnchorRef.current?.token !== token) return;

    pendingAnchorExpiryRef.current = setTimeout(() => {
      pendingAnchorExpiryRef.current = null;
      if (pendingAnchorRef.current?.token === token) {
        pendingAnchorRef.current = null;
      }
    }, 0);
  };

  const selectDirectAction = (action: MarketWidgetAction, anchor: HTMLButtonElement) => {
    if (action.locked) {
      onLockedAction?.(action);
      return;
    }
    if (action.disabled) return;

    const pendingAnchorToken = prepareReturnAnchor(anchor.id);
    try {
      action.onSelect(anchor);
    } finally {
      scheduleReturnAnchorExpiry(pendingAnchorToken);
    }
  };

  const selectOverflowAction = (action: MarketWidgetAction) => {
    const anchor = overflowTriggerRef.current;
    if (!anchor) return;

    if (action.locked) {
      onLockedAction?.(action);
      return;
    }
    if (action.disabled) return;

    const pendingAnchorToken = prepareReturnAnchor(overflowAnchorId);
    try {
      action.onSelect(anchor);
    } finally {
      scheduleReturnAnchorExpiry(pendingAnchorToken);
    }
  };

  const hasVisibleActions = actions.some(action => action.visible);
  const cardClassName = [
    'h-full min-h-0 gap-0 py-0 overflow-hidden',
    fullWidth ? 'lg:col-span-2' : '',
    fullscreen ? 'h-full' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const surface = (
    <Card
      aria-label={title}
      className={cardClassName}
      role='region'
    >
      <Tabs
        className='min-h-0 flex-1 gap-0'
        value={String(activeTab)}
        onValueChange={value => onTabChange(Number(value))}
      >
        <CardHeader className='shrink-0 p-0 gap-0'>
          {hasVisibleActions ? (
            <CardAction>
              <ActionControls
                actions={actions}
                directAnchorId={directAnchorId}
                overflowAnchorId={overflowAnchorId}
                overflowTriggerRef={overflowTriggerRef}
                onDirectAction={selectDirectAction}
                onOverflowAction={selectOverflowAction}
              />
            </CardAction>
          ) : null}
          <TabsList
            activateOnFocus
            variant='underline'
          >
            {tabs.map((tab, index) => (
              <TabsTrigger
                key={tab.id}
                value={String(index)}
                aria-disabled={tab.disabled || undefined}
                disabled={tab.disabled}
              >
                {tab.name}
                {tab.tag}
              </TabsTrigger>
            ))}
          </TabsList>
        </CardHeader>
        <Separator />
        <CardContent className='min-h-0 flex-1 overflow-hidden p-0 [&_*]:[scrollbar-color:#8589b5_transparent] [&_*]:[scrollbar-width:thin] [&_*::-webkit-scrollbar]:h-1.5 [&_*::-webkit-scrollbar]:w-1.5 [&_*::-webkit-scrollbar-thumb]:rounded-full [&_*::-webkit-scrollbar-thumb]:bg-[#8589b5]'>
          {children}
        </CardContent>
      </Tabs>
    </Card>
  );

  if (!fullscreen) return surface;

  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open) onRequestCloseFullscreen();
      }}
    >
      <DialogContent
        ref={dialogContentRef}
        aria-describedby={undefined}
        className='inset-0 size-full max-w-none translate-x-0 translate-y-0 rounded-none'
        finalFocus={false}
        initialFocus={dialogContentRef}
        showCloseButton={false}
      >
        <DialogTitle className='sr-only'>{title}</DialogTitle>
        <DialogClose
          render={
            <IconButton
              className='absolute top-4 right-4 z-10'
              label='Close fullscreen'
            />
          }
        >
          <XIcon />
        </DialogClose>
        {surface}
      </DialogContent>
    </Dialog>
  );
}

export default MarketWidgetSurface;
